import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
} from "@mui/material";
import { AppDialog } from "components/common/dialogs";
import { AppSelect, AppTextField } from "components/common/inputs";
import { AppButton } from "components/common/buttons";

const getInitialValues = (fields, initialValues) =>
  fields.reduce((values, field) => {
    if (field.type === "switch") {
      values[field.name] = initialValues?.[field.name] ?? field.defaultValue ?? false;
    } else if (field.type === "select" && field.multiple) {
      const initialValue = initialValues?.[field.name] ?? field.defaultValue ?? [];
      values[field.name] = Array.isArray(initialValue) ? initialValue : initialValue ? [initialValue] : [];
    } else {
      values[field.name] = initialValues?.[field.name] ?? field.defaultValue ?? "";
    }
    return values;
  }, {});

const CatalogFormDialog = ({
  open,
  title,
  fields,
  initialValues,
  submitting,
  onClose,
  onBeforeChange,
  onSubmit,
}) => {
  const initial = useMemo(() => getInitialValues(fields, initialValues), [fields, initialValues]);
  const [values, setValues] = useState(initial);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setValues(initial);
      setErrors({});
    }
  }, [initial, open]);

  const handleChange = (name, value) => {
    const nextValues = { ...values, [name]: value };
    if (onBeforeChange?.(name, value, nextValues, values) === false) return;
    setValues(nextValues);
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const handleSubmit = () => {
    const nextErrors = {};
    fields.forEach((field) => {
      const value = values[field.name];
      const isEmptyMultiple = field.multiple && Array.isArray(value) && value.length === 0;
      const isEmptySingle = !field.multiple && !String(value ?? "").trim();
      if (field.required && (isEmptyMultiple || isEmptySingle)) {
        nextErrors[field.name] = "Vui lòng nhập thông tin";
      }
      if (field.maxLength && String(value ?? "").length > field.maxLength) {
        nextErrors[field.name] = `Tối đa ${field.maxLength} ký tự`;
      }
    });

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSubmit(values);
  };

  return (
    <AppDialog open={open} onClose={onClose} title={title} size="sm" loading={submitting}
      actions={<>
        <AppButton variant="outlined" onClick={onClose} disabled={submitting}>Hủy</AppButton>
        <AppButton onClick={handleSubmit} variant="contained" disabled={submitting}>
          {submitting ? <CircularProgress size={22} /> : "Lưu"}
        </AppButton>
      </>}>
      <Stack spacing={2} sx={{ pt: 1 }}>
        {fields.map((field) => {
          if (field.type === "select") {
            return (
              <AppSelect
                key={field.name}
                label={field.label}
                value={values[field.name] ?? (field.multiple ? [] : "")}
                options={field.options}
                required={field.required}
                disabled={submitting || field.disabled}
                error={Boolean(errors[field.name])}
                helperText={errors[field.name]}
                multiple={Boolean(field.multiple)}
                onChange={(selectedValue) => handleChange(field.name, selectedValue)}
              />
            );
          }

          if (field.type === "switch") {
            return (
              <FormControlLabel
                key={field.name}
                control={
                  <Switch
                    checked={Boolean(values[field.name])}
                    onChange={(event) => handleChange(field.name, event.target.checked)}
                    disabled={submitting || field.disabled}
                  />
                }
                label={field.label}
              />
            );
          }

          return (
            <AppTextField
              key={field.name}
              name={field.name}
              label={field.label}
              value={values[field.name] ?? ""}
              onChange={(value) => handleChange(field.name, value)}
              required={field.required}
              error={Boolean(errors[field.name])}
              helperText={errors[field.name]}
              disabled={submitting || field.disabled}
              multiline={field.multiline}
              minRows={field.multiline ? 2 : undefined}
              type={field.type === "number" ? "number" : "text"}
              maxLength={field.maxLength}
            />
          );
        })}
      </Stack>
    </AppDialog>
  );
};

export default CatalogFormDialog;
